import { Component, DestroyRef, effect, input, model, output, signal, untracked } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Pet, genders, species, temperament } from '../../../shared/models/pet.model';
import { sanitizeFields, validateBlank, validateYearsLimitFromToday, watchFormRecursively } from '../../../shared/utils/form-utils';

import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FormFieldFeedbackPipe } from '../../../pipes/form-field-feedback/form-field-feedback.pipe';
import { ImageModule } from 'primeng/image';
import { InputText } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-pet-form',
  imports: [
    SelectModule,
    ButtonModule,
    ImageModule,
    InputText,
    SelectButtonModule,
    DatePickerModule,
    FormFieldFeedbackPipe,
    TooltipModule,
    ReactiveFormsModule,
    MultiSelectModule,
    FloatLabelModule
  ],
  templateUrl: './pet-form.component.html',
  styleUrl: './pet-form.component.scss'
})
export class PetFormComponent {
  readonly petForm = new FormGroup({
    name: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    birthdate: new FormControl<Date>(new Date(), {
      validators: [
        Validators.required,
        validateYearsLimitFromToday(100, true)
      ]
    }),
    gender: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    specie: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    breed: new FormControl<string>(''),
    temperament: new FormControl<string[]>([], {
      validators: [
        Validators.required,
        Validators.minLength(1)
      ]
    }),
    foodType: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    })
  });

  readonly species = species;
  readonly genders = genders;
  readonly temperament = temperament;
  readonly today = new Date();
    readonly id = uuidv4();


  readonly maxFiles = input<number>(3);
  readonly initialData = input<Pet | null>(null);
  readonly onSubmit = output<any>();
  readonly enabled = model<boolean>(true);
  readonly isLoading = model.required<boolean>();

  readonly formWatched = watchFormRecursively(this.petForm);

  filesWrapper = signal<{ file: File, previewUrl: string }[]>([]);

  constructor(private readonly messageService: MessageService, private readonly destroyRef: DestroyRef) {
    effect(() => {
      if (this.enabled()) {
        this.petForm.enable();
      } else {
        this.petForm.disable();
      }
    });

    effect(() => {
      if (this.initialData()) {
        this.enabled.set(false);
        this.petForm.patchValue(this.initialData()!);

        if (this.initialData()!.files.length) {
          Promise.all(
            this.initialData()!.files.map(async (url) => {
              try {
                const response = await fetch(url);
                const blob = await response.blob();
                const fileName = decodeURIComponent(url).split('?')[0].split('/').pop() ?? 'image';

                const file = new File([blob], fileName, { type: blob.type });
                return {
                  file,
                  previewUrl: URL.createObjectURL(file)
                };
              } catch (error) {
                console.error(`Error fetching image from pet ${this.initialData()!.id}:`, error);
                return null;
              }
            })
          ).then(wrappers => {
            this.filesWrapper.set(wrappers.filter(wrapper => wrapper !== null));
          });
        }
      } else {
        this.enabled.set(true);
        this.resetForm();
      }
    })

    this.destroyRef.onDestroy(() => {
      this.filesWrapper().forEach(wrapper => URL.revokeObjectURL(wrapper.previewUrl));
    });
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    let newFiles: any = Array.from(input.files);

    if (this.filesWrapper().length + newFiles.length > this.maxFiles()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: `O número máximo de arquivos é ${this.maxFiles()}!`,
        life: 4000
      });
      return;
    }

    newFiles = newFiles.map((file: File) => ({
      file,
      previewUrl: URL.createObjectURL(file)
    }));

    this.filesWrapper.update(curr => [...curr, ...newFiles]);
  }

  onRemoveFile(index: number) {
    const fileToRemove = this.filesWrapper()[index];

    if (fileToRemove) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
      this.filesWrapper.update(prev => prev.filter((_, i) => i !== index));
    }
  }

  resetForm(): void {
    this.petForm.reset();

    untracked(() => {
      this.filesWrapper().forEach(wrapper => URL.revokeObjectURL(wrapper.previewUrl));
    })

    this.filesWrapper.set([]);
    this.isLoading.set(false);
  }

  submitForm(): void {
    this.isLoading.set(true);

    if (this.petForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todos os campos obrigatórios e adicione pelo menos uma imagem!',
        life: 4000
      });
      this.isLoading.set(false);
      return;
    }

    sanitizeFields(this.petForm);
    this.onSubmit.emit({
      ...this.petForm.value,
      files: this.filesWrapper().map(wrapper => wrapper.file)
    });
  }
}
